﻿import ApiService from 'services/ApiService'

const badRequestCode = 400;

function getTemplateApiUrl(useGlobalEndpoint) {
    return useGlobalEndpoint ? "/check/global/templates" : "/check/templates";
}

export const CheckService = {
    getAllTemplates(searchObject, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.post(
                `${apiUrl}/search`, searchObject).then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        if (error.response.status === badRequestCode) {
                            reject(error.response);
                        } else {
                            reject(
                                "Unable to search templates. Please try again later."
                            );
                        }
                    }
                );
        });
    },

    getTemplate(templateId, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.get(`${apiUrl}/${templateId}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error));
        })
    },

    getTemplateOptions() {
        return new Promise((resolve, reject) => {
            ApiService.get('/check/options/templates').then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    reject("Unable to get template categories. Please try again later.");
                }
            );
        });
    },

    // Helper method to extract subcategories from the response
    extractSubCategories(response) {
        const subCategories = {};

        // Extract subcategories from templateCategories if they exist
        if (response.templateCategories && response.templateCategories.length > 0) {
            response.templateCategories.forEach(category => {
                if (category.subCategories && Array.isArray(category.subCategories)) {
                    subCategories[category.id] = category.subCategories;
                }
            });
        }

        return subCategories;
    },

    createTemplate(template, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.post(apiUrl, template).then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        reject(error.response.data);
                    } else {
                        reject("Unable to create template. Please try again later.");
                    }
                }
            );
        });
    },

    updateTemplate(template, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.put(`${apiUrl}/${template.id}`, template).then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        reject(error.response.data);
                    } else {
                        reject("Unable to update template. Please try again later.");
                    }
                }
            );
        });
    },

    getTemplateChangelog(templateId, pageSize, pageNumber, startDate, endDate, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.get(`${apiUrl}/${templateId}/changelog?resultCount=${pageSize}&pageNumber=${pageNumber}&startDate=${startDate}&endDate=${endDate}`).then(
                (result) => {
                    resolve(result.data);
                },
                (error) => {
                    reject(error);
                });
        });
    },

    getTemplateLayout(templateId, useGlobalEndpoint) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.get(`${apiUrl}/${templateId}/layout`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    saveTemplateLayout(templateId, layoutData, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.put(`${apiUrl}/${templateId}/layout`, layoutData)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    setTemplateStatus(templateId, status, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.patch(`${apiUrl}/${templateId}/status`, status)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    cloneTemplate(templateId, newTemplateName, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.post(`${apiUrl}/${templateId}/clone`, newTemplateName)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    getAllChecks(pageNumber, requestCount, searchTerm, sortField, sortDirection, filters) {
        let searchObject = {
            "requestCount": requestCount,
            "pageNumber": pageNumber,
            "searchTerm": searchTerm,
            "sortField": sortField,
            "sortDirection": sortDirection,
            "filters": filters
        };

        return new Promise((resolve, reject) => {
            ApiService.post(
                `/check/checks/search`,
                searchObject)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        if (error.response.status === badRequestCode) {
                            reject(error.response);
                        } else {
                            reject(
                                "Unable to search checks. Please try again later."
                            );
                        }
                    }
                );
        });
    },

    getTemplatesFilterOptions() {
        return new Promise((resolve, reject) => {
            ApiService.get('/check/options/templates/filter').then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    reject("Unable to get templates filter options. Please try again later.");
                }
            );
        });
    },


    getTemplateVersions(id, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.get(`${apiUrl}/${id}/versions`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    createNewVersion(id, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.post(`${apiUrl}/${id}/newversion`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    getCheckSettings() {
        return new Promise((resolve, reject) => {
            ApiService.get('/check/settings').then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    reject("Unable to get check settings. Please try again later.");
                }
            );
        });
    },

    saveCheckSettings(checkSettings) {
        return new Promise((resolve, reject) => {
            ApiService.put('/check/settings', checkSettings).then(
                (response) => {
                    resolve(response.data);
                },
                (error) => {
                    if (error.response.status === badRequestCode) {
                        reject(error.response.data);
                    } else {
                        reject("Unable to save check settings. Please try again later.");
                    }
                }
            );
        });
    },

    getCheckSettingsChangelog(pageSize, pageNumber, startDate, endDate) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/check/settings/changelog?resultCount=${pageSize}&pageNumber=${pageNumber}&startDate=${startDate}&endDate=${endDate}`).then(
                (result) => {
                    resolve(result.data);
                },
                (error) => {
                    reject(error);
                });
        });
    },

    getCheck(id) {
        return new Promise((resolve, reject) => {
            ApiService.get(`/check/checks/${id}`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    signOffDefect(checkId, defectId, defectSignOff) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/check/checks/${checkId}/defects/${defectId}/signoff`, defectSignOff)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },

    signOffCheck(checkId) {
        return new Promise((resolve, reject) => {
            ApiService.patch(`/check/checks/${checkId}/signoff`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    generateCheckReport(checkId) {
        return new Promise((resolve, reject) => {
            ApiService.post(`/check/checks/${checkId}/Report`).then(
                (result) => {
                    resolve(result.data);
                },
                (error) => {
                    reject(error.response);
                }
            );
        });
    },
    getTemplateMedia(templateId, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.get(`${apiUrl}/${templateId}/media`)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    uploadTemplateMedia(templateId, mediaReference, file, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        const formData = new FormData();

        formData.append("File", file);
        formData.append("MediaReference", mediaReference);
        formData.append("MediaType", "HelperText");

        return new Promise((resolve, reject) => {
            ApiService.post(`${apiUrl}/${templateId}/media`, formData)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    updateTemplateMedia(templateId, addedMedia, removedMedia, useGlobalEndpoint = false) {
        const apiUrl = getTemplateApiUrl(useGlobalEndpoint);
        return new Promise((resolve, reject) => {
            ApiService.put(`${apiUrl}/${templateId}/media`, { addedMedia, removedMedia })
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
}